import {
  AnalyticsContext,
  ConfigContext,
  OrganisationContext,
} from '@/context';
import { useMutation, usePermission } from '@/hooks';
import { ADD_DOCUMENT, UPDATE_PROFILE_DATA_MUTATION } from '@/mutations';
import {
  GET_USER_PROFILE_DOCUMENTS,
  GET_USER_PROFILE_VERSIONS,
} from '@/queries';
import { getRequest } from '@/services';
import { AppToaster } from '@/utils/toaster';
import { Classes, ProgressBar } from '@blueprintjs/core';
import permissions from '@hogwarts/permissions';
import cn from 'classnames';
import { init } from 'filestack-js';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { transformProfileVersionData } from '../../containers/userProfile/utils';
import { UserContext } from '../../context';
import { DELETE_PROFILE_PICTURE } from '../../mutations';
import { GET_USER_PROFILE } from '../../queries';

const renderProgress = (name, amount) => {
  return {
    icon: 'cloud-upload',
    message: (
      <div>
        <p>Uploading {name}</p>
        <ProgressBar
          className={cn({
            [Classes.PROGRESS_NO_STRIPES]: amount >= 100,
          })}
          intent={amount < 100 ? 'primary' : 'success'}
          value={amount / 100}
        />
      </div>
    ),
    timeout: amount < 100 ? 0 : 2000,
  };
};

/**
 * Validate file type against allowed MIME types
 * @param {File} file - The file to validate
 * @param {string[]} allowedMimeTypes - Array of allowed MIME types
 * @returns {boolean} - True if file type is allowed
 */
const validateFileType = (file, allowedMimeTypes) => {
  if (!Array.isArray(allowedMimeTypes) || allowedMimeTypes.length === 0) {
    return true; // If no restrictions, allow all
  }
  return allowedMimeTypes.includes(file.type);
};

const DOCUMENT_ACCEPT_PATTERNS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];

/** Display key for Filestack picker callbacks (varies: `filename`, `name`, etc.). */
const getPickerFileKey = (file) => {
  if (!file) return '';
  return (
    file.filename ||
    file.name ||
    (typeof file.originalPath === 'string'
      ? file.originalPath.split('/').filter(Boolean).pop()
      : '') ||
    ''
  );
};

const getPickerResultKey = (result) => {
  if (!result) return '';
  return result.filename || result.name || '';
};

const getNotifier = (filename, t) => ({
  uploadStarted: () => {
    AppToaster.show(renderProgress(filename, 0), filename);
  },
  uploadProgress: (percentage) => {
    AppToaster.show(renderProgress(filename, percentage), filename);
  },
  uploadSuccess: () => {
    AppToaster.show(
      {
        icon: 'tick',
        intent: 'success',
        message: `${t('Successfully uploaded')} ${filename}`,
      },
      filename
    );
  },
  uploadError: (errorMessage) => {
    const message = errorMessage 
      ? `${t('Failed to upload')} ${filename}: ${errorMessage}`
      : `${t('Failed to upload')} ${filename}`;
    AppToaster.show(
      {
        icon: 'error',
        intent: 'danger',
        message,
      },
      filename
    );
  },
});

const getErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;

  const response = error?.response;
  if (response) {
    const data = response.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data?.message) return data.message;
    if (response.status) return `Request failed with status code ${response.status}`;
  }

  return error?.message || error?.toString?.() || 'Unknown error';
};

const showToastOnError = (message) => {
  AppToaster.show({
    icon: 'error',
    intent: 'danger',
    message,
  });
  return null;
};

const getUploadPolicy = async (organisationId, t) => {
  try {
    const request = await getRequest();
    const policyResponse = await request.get(
      `/documents/policy/${organisationId}/upload`
    );

    if (policyResponse.status === 200) {
      return policyResponse.data;
    } else {
      return showToastOnError(t('Error starting upload'));
    }
  } catch (error) {
    return showToastOnError(t('Error starting upload'));
  }
};

const beginDocumentUploadSession = async (
  { ownerType, ownerId, tags },
  t
) => {
  try {
    const request = await getRequest();
    const sessionResponse = await request.post('/documents/upload-session', {
      ownerType,
      ownerId,
      tags,
    });

    if (sessionResponse.status === 200) {
      return sessionResponse.data;
    }

    return showToastOnError(t('Error starting upload'));
  } catch (error) {
    return showToastOnError(
      `${t('Error starting upload')}: ${getErrorMessage(error)}`
    );
  }
};

const completeDocumentUploadSession = async (sessionId, result) => {
  try {
    const request = await getRequest();
    const response = await request.post(
      `/documents/upload-session/${sessionId}/complete`,
      {
        filestackHandle: result.handle,
        filename: result.filename,
        size: result.size,
        type: result.mimetype ?? result.type ?? 'Unknown',
      }
    );

    if (response.status !== 200) {
      throw new Error(
        (typeof response.data === 'string' && response.data) ||
          response.data?.message ||
          'Failed to complete upload'
      );
    }

    return response.data.uploadReference;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
};

const getRegisteredDocumentId = (mutationResult) => {
  const data = mutationResult?.data;
  if (!data) {
    return null;
  }
  if (typeof data.id === 'string') {
    return data.id;
  }
  if (typeof data.addDocument?.id === 'string') {
    return data.addDocument.id;
  }
  return null;
};

const registerUploadedDocument = async ({
  result,
  ownerType,
  ownerId,
  tags,
  meta,
  addDocument,
  t,
}) => {
  // One Redis upload session consumes one completion; multi-file uploads need a fresh session per file.
  const sessionData = await beginDocumentUploadSession(
    { ownerType, ownerId, tags },
    t
  );
  if (!sessionData) {
    throw new Error(t('Error starting upload'));
  }

  const { sessionId } = sessionData;
  const uploadReference = await completeDocumentUploadSession(sessionId, result);

  return addDocument({
    variables: {
      document: {
        uploadReference,
        ownerType,
        ownerId,
        meta,
      },
    },
  });
};

const UploadOrgDocument = (Component) => {
  const UploadOrganisationDocumentWrapper = ({ ...props }) => {
    const [documents, setDocuments] = useState([]);
    const config = useContext(ConfigContext);
    const organisation = useContext(OrganisationContext);
    const user = useContext(UserContext);
    const analytics = useContext(AnalyticsContext);
    const { t } = useTranslation();
    const [addDocument] = useMutation(ADD_DOCUMENT, {
      selector: 'addDocument',
    });
    const allowUploadFile = usePermission(
      permissions.ORGANISATION_FILE_CREATE,
      organisation.id
    );

    return (
      <Component
        {...props}
        resetDocuments={() => setDocuments([])}
        onRemoveDocument={(document) => {
          setDocuments(documents.filter((d) => d.id !== document.id));
        }}
        documents={documents}
        allowUpload={allowUploadFile}
        onOpenDocumentPicker={async ({ confidential } = {}) => {
          if (!allowUploadFile) {
            return;
          }

          analytics.events.documents.uploadClicked();
          const tags = [];
          if (confidential) {
            tags.push('confidential');
          }
          const sessionData = await beginDocumentUploadSession(
            {
              ownerType: 'ORGANISATION',
              ownerId: organisation.id,
              tags,
            },
            t
          );
          if (sessionData) {
            // Session Redis key persists until TTL unless consumed by registerUploadedDocument — we only need policy here:
            const { policy: policyData } = sessionData;
            const client = init(config.FILESTACK_APIKEY, {
              security: policyData,
              tags: {
                type: 'document',
                organisationId: organisation.id,
              },
            });
            const notifiers = {};
            client
              .picker({
                storeTo: {
                  path: `organisations/${organisation.id}/documents/`,
                  workflows: [config.FILESTACK_VIRUSSCAN_WORKFLOW],
                },
                accept: DOCUMENT_ACCEPT_PATTERNS,
                maxFiles: 10,
                onFileUploadStarted: (file) => {
                  const key = getPickerFileKey(file);
                  notifiers[key] = getNotifier(key || file?.name || t('File'), t);
                  notifiers[key].uploadStarted();
                },
                onFileUploadProgress: (file, event) => {
                  const key = getPickerFileKey(file);
                  const notifier = notifiers[key];
                  if (!notifier) return;
                  notifier.uploadProgress(event?.totalPercent ?? 0);
                },
                onFileUploadFinished: async (result) => {
                  const resultKey = getPickerResultKey(result);
                  try {
                    const storedDocument = await registerUploadedDocument({
                      result,
                      ownerType: 'ORGANISATION',
                      ownerId: organisation.id,
                      tags,
                      meta: {
                        uploadedBy: user.id,
                        organisationName: organisation.name,
                      },
                      addDocument,
                      t,
                    });
                    const documentId = getRegisteredDocumentId(storedDocument);
                    if (!documentId) {
                      throw new Error(
                        t(
                          'Document uploaded but could not be registered. Please try again.'
                        )
                      );
                    }
                    analytics.events.documents.fileUploaded();
                    notifiers[resultKey]?.uploadSuccess();
                    setDocuments((documents) => [
                      ...documents,
                      {
                        id: documentId,
                        filename: result.filename,
                        size: result.size,
                        type: result.mimetype ?? 'Unknown',
                        tags,
                      },
                    ]);
                  } catch (error) {
                    const errorMessage = getErrorMessage(error);
                    notifiers[resultKey]?.uploadError(errorMessage);
                  }
                },
                onFileUploadFailed: (file, error) => {
                  const key = getPickerFileKey(file);
                  const errorMessage =
                    error?.message || error?.toString() || 'Filestack upload error';
                  if (notifiers[key]) {
                    notifiers[key].uploadError(errorMessage);
                  } else {
                    showToastOnError(errorMessage);
                  }
                },
              })
              .open();
          }
        }}
      />
    );
  };

  return UploadOrganisationDocumentWrapper;
};

const UploadProfilePictureHoc = (Component) =>
  function UploadPictureContainer({
    profile: profileItem,
    allowEdit,
    ...props
  }) {
    let refetchQueries = [];
    const profile = profileItem || props.item || {};
    const [updatedImage, setUpdatedImage] = useState(profile.image);
    const config = useContext(ConfigContext);
    const organisation = useContext(OrganisationContext);
    const analytics = useContext(AnalyticsContext);
    const { t } = useTranslation();

    const [editProfile] = useMutation(UPDATE_PROFILE_DATA_MUTATION);
    const [deleteProfilePicture] = useMutation(DELETE_PROFILE_PICTURE, {
      refetchQueries: [
        {
          query: GET_USER_PROFILE,
          variables: {
            organisationKey: organisation.key,
            profileId: profile?.id,
          },
        },
      ],
    });

    if (!props.skipProfileVersionRefetch) {
      refetchQueries = [
        {
          query: GET_USER_PROFILE_VERSIONS,
          variables: {
            organisationKey: organisation?.key,
            profileId: profile?.id,
            limit: 15,
            changes: ['profileTypeKey', 'organisation', 'data', 'add'],
            cursor: null,
          },
          selector: 'organisations[0].profiles[0].versions',
          transform: transformProfileVersionData(props.scheme),
        },
      ];
    }

    return (
      <Component
        {...props}
        updatedImage={updatedImage}
        profile={profile}
        allowEdit={allowEdit}
        onProfilePictureUpload={async ({ files }) => {
          if (!allowEdit) {
            return;
          }
          if (!files.length) return;
          analytics.events.profile.pictureDropped();

          if (Array.isArray(files) && files.length === 1) {
            const policyData = await getUploadPolicy(organisation.id, t);
            if (policyData) {
              const client = init(config.FILESTACK_APIKEY, {
                security: policyData,
                tags: {
                  type: 'profile_picture',
                  profileId: profile.id,
                },
              });

              files.forEach(async (file) => {
                if (!file.type.startsWith('image/')) {
                  return;
                }
                const notifier = getNotifier(file.name, t);
                
                // Validate file type before upload (profile pictures should be images)
                if (!validateFileType(file, config.IMAGE_ALLOWED_MIME_TYPES)) {
                  AppToaster.show({
                    icon: 'error',
                    intent: 'danger',
                    message: `File type "${file.type}" is not allowed for profile pictures. Allowed types: ${(config.IMAGE_ALLOWED_MIME_TYPES || []).join(', ')}`,
                  });
                  notifier.uploadError();
                  return;
                }
                
                notifier.uploadStarted();
                try {
                  const result = await client.upload(
                    file,
                    {
                      onProgress: (event) => {
                        notifier.uploadProgress(event.totalPercent);
                      },
                      progressInterval: 100,
                    },
                    {
                      path: `profiles/${profile.id}/profilepic/`,
                    }
                  );
                  await editProfile({
                    refetchQueries,
                    variables: {
                      profileId: profile.id,
                      data: {
                        picture: result.url,
                      },
                      meta: {
                        picture: {
                          filename: result.filename,
                          size: result.size,
                          type: result.type,
                          filestackHandle: result.handle,
                        },
                      },
                      trace: [
                        {
                          component: 'UploadProfilePictureContainer',
                          event: 'pictureDropped(Multiple)',
                        },
                      ],
                    },
                  });
                  analytics.events.profile.pictureUpload();
                  notifier.uploadSuccess();
                  setUpdatedImage(result.url);
                } catch (error) {
                  notifier.uploadError();
                }
              });
            }
          }
        }}
        onOpenProfilePicturePicker={async () => {
          if (!allowEdit) {
            return;
          }
          analytics.events.profile.pictureUploadClicked();
          const policyData = await getUploadPolicy(organisation.id, t);
          if (policyData) {
            const client = init(config.FILESTACK_APIKEY, {
              security: policyData,
              tags: {
                type: 'profile_picture',
                profileId: profile.id,
              },
            });
            const notifiers = {};
            client
              .picker({
                storeTo: {
                  path: `profiles/${profile.id}/profilepic/`,
                  workflows: [config.FILESTACK_VIRUSSCAN_WORKFLOW],
                },
                accept: ['image/*'],
                maxFiles: 1,
                onFileUploadStarted: (file) => {
                  const key = getPickerFileKey(file);
                  notifiers[key] = getNotifier(key || file?.name || t('File'), t);
                  notifiers[key].uploadStarted();
                },
                onFileUploadProgress: (file, event) => {
                  const key = getPickerFileKey(file);
                  const notifier = notifiers[key];
                  if (!notifier) return;
                  notifier.uploadProgress(event?.totalPercent ?? 0);
                },
                onFileUploadFinished: async (result) => {
                  const resultKey = getPickerResultKey(result);
                  try {
                    await editProfile({
                      refetchQueries,
                      variables: {
                        profileId: profile.id,
                        data: {
                          picture: result.url,
                        },
                        meta: {
                          picture: {
                            filename: result.filename,
                            size: result.size,
                            type: result.type,
                            filestackHandle: result.handle,
                          },
                        },
                        trace: [
                          {
                            component: 'UploadProfilePictureContainer',
                            event: 'pictureDropped',
                          },
                        ],
                      },
                    });
                    analytics.events.profile.pictureUpload();
                    notifiers[resultKey]?.uploadSuccess();
                    setUpdatedImage(result.url);
                  } catch (error) {
                    notifiers[resultKey]?.uploadError();
                  }
                },
                onFileUploadFailed: (file, error) => {
                  const key = getPickerFileKey(file);
                  const errorMessage =
                    error?.message || error?.toString() || 'Filestack upload error';
                  if (notifiers[key]) {
                    notifiers[key].uploadError(errorMessage);
                  } else {
                    showToastOnError(errorMessage);
                  }
                },
              })
              .open();
          }
        }}
        onDeleteProfilePicture={async () => {
          analytics.events.profile.pictureDeleteClicked();

          // Delete profile picture from Filestack and DB
          const deleteSuccess = await deleteProfilePicture({
            variables: {
              profileId: profile.id,
            },
          });

          if (deleteSuccess.data.deleteProfilePicture) setUpdatedImage(null);

          if (!deleteSuccess.data) {
            AppToaster.show({
              icon: 'error',
              intent: 'danger',
              message: t('Error deleting profile picture.'),
            });
          }
        }}
      />
    );
  };

const UploadContainerHoC = (Component) =>
  function UploadContainer({ profile, currentDocumentsFilter, ...props }) {
    const config = useContext(ConfigContext);
    const organisation = useContext(OrganisationContext);
    const analytics = useContext(AnalyticsContext);
    const { t } = useTranslation();
    const [addDocument] = useMutation(ADD_DOCUMENT, {
      selector: 'addDocument',
      refetchQueries: [
        {
          query: GET_USER_PROFILE_DOCUMENTS,
          variables: {
            organisationKey: organisation.key,
            profileId: profile.id,
          },
        },
      ],
    });
    const allowUploadFile = usePermission(
      permissions.PROFILE_FILE_CREATE,
      organisation.id
    );

    return (
      <Component
        {...props}
        profile={profile}
        allowUpload={allowUploadFile}
        onDocumentsUpload={async ({ sectionKey, files, confidential }) => {
          if (!allowUploadFile) {
            return;
          }
          analytics.events.documents.fileDropped({ sectionKey, confidential });
          if (Array.isArray(files) && files.length > 0) {
            const tags = [];
            if (confidential) {
              tags.push('confidential');
            }
            const sessionData = await beginDocumentUploadSession(
              {
                ownerType: 'PROFILE',
                ownerId: profile.id,
                tags,
              },
              t
            );
            if (sessionData) {
              const { policy: policyData } = sessionData;
              const client = init(config.FILESTACK_APIKEY, {
                security: policyData,
                tags: {
                  type: 'document',
                  profileId: profile.id,
                },
              });
              files.forEach(async (file) => {
                const notifier = getNotifier(file.name, t);
                
                if (!validateFileType(file, config.DOCUMENT_ALLOWED_MIME_TYPES)) {
                  AppToaster.show({
                    icon: 'error',
                    intent: 'danger',
                    message: `File type "${file.type}" is not allowed. Allowed types: ${(config.DOCUMENT_ALLOWED_MIME_TYPES || []).join(', ')}`,
                  });
                  notifier.uploadError();
                  return;
                }
                
                notifier.uploadStarted();
                try {
                  const result = await client.upload(
                    file,
                    {
                      onProgress: (event) => {
                        notifier.uploadProgress(event.totalPercent);
                      },
                      progressInterval: 100,
                    },
                    {
                      path: `profiles/${profile.id}/documents/`,
                    }
                  );
                  await registerUploadedDocument({
                    result,
                    ownerType: 'PROFILE',
                    ownerId: profile.id,
                    tags,
                    meta: {
                      sectionKey,
                    },
                    addDocument,
                    t,
                  });
                  analytics.events.documents.fileUploaded({
                    sectionKey,
                    confidential,
                  });
                  notifier.uploadSuccess();
                } catch (error) {
                  notifier.uploadError();
                }
              });
            }
          }
        }}
        onOpenDocumentPicker={async ({ confidential } = {}) => {
          if (!allowUploadFile) {
            return;
          }
          analytics.events.documents.uploadClicked();
          const tags = [];
          if (confidential) {
            tags.push('confidential');
          }
          const sessionData = await beginDocumentUploadSession(
            {
              ownerType: 'PROFILE',
              ownerId: profile.id,
              tags,
            },
            t
          );
          if (sessionData) {
            const { policy: policyData } = sessionData;
            const client = init(config.FILESTACK_APIKEY, {
              security: policyData,
              tags: {
                type: 'document',
                profileId: profile.id,
              },
            });
            const notifiers = {};
            client
              .picker({
                storeTo: {
                  path: `profiles/${profile.id}/documents/`,
                  workflows: [config.FILESTACK_VIRUSSCAN_WORKFLOW],
                },
                accept: DOCUMENT_ACCEPT_PATTERNS,
                maxFiles: 10,
                onFileUploadStarted: (file) => {
                  const key = getPickerFileKey(file);
                  notifiers[key] = getNotifier(key || file?.name || t('File'), t);
                  notifiers[key].uploadStarted();
                },
                onFileUploadProgress: (file, event) => {
                  const key = getPickerFileKey(file);
                  const notifier = notifiers[key];
                  if (!notifier) return;
                  notifier.uploadProgress(event?.totalPercent ?? 0);
                },
                onFileUploadFinished: async (result) => {
                  const resultKey = getPickerResultKey(result);
                  try {
                    const tags = [];
                    if (confidential) {
                      tags.push('confidential');
                    }
                    await registerUploadedDocument({
                      result,
                      ownerType: 'PROFILE',
                      ownerId: profile.id,
                      tags,
                      meta: {},
                      addDocument,
                      t,
                    });
                    analytics.events.documents.fileUploaded();
                    notifiers[resultKey]?.uploadSuccess();
                  } catch (error) {
                    const errorMessage =
                      error?.message || error?.toString() || 'Unknown error';
                    notifiers[resultKey]?.uploadError(errorMessage);
                  }
                },
                onFileUploadFailed: (file, error) => {
                  const key = getPickerFileKey(file);
                  const errorMessage =
                    error?.message || error?.toString() || 'Filestack upload error';
                  if (notifiers[key]) {
                    notifiers[key].uploadError(errorMessage);
                  } else {
                    showToastOnError(errorMessage);
                  }
                },
              })
              .open();
          }
        }}
      />
    );
  };

export const UploadOrganisationDocument = (Component) =>
  UploadOrgDocument(Component);
export const UploadProfilePicture = (Component) =>
  UploadProfilePictureHoc(Component);
export const UploadProfileDocument = (Component) =>
  UploadProfilePictureHoc(UploadContainerHoC(Component));
